import {
  FlagProvider,
  IToggle,
  useUnleashContext,
  useVariant,
} from '@unleash/nextjs';
import { useRouter } from 'next/router';
import { ReactNode, useEffect } from 'react';
import {
  ONBOARDING_PATHS,
  PUBLIC_PATHS,
  SUBSCRIPTION_PATHS,
  UNLEASH_TOGGLES,
} from '@/common/constants';
import Maintenance from '@/modules/auth/components/Maintenance';
import useMyUserCompanyQuery from '@/modules/company/hooks/queries/useMyUserCompanyQuery';
import { UNLEASH_CONFIG } from '../configs';

export const generateUnleashContext = (
  userCompanyEmail: string | null,
  userCompanyEmailDomain: string | null,
) => {
  return {
    ...(!!userCompanyEmail && { userId: userCompanyEmail }),
    properties: {
      ...(!!userCompanyEmailDomain && {
        companyEmailDomain: userCompanyEmailDomain,
      }),
    },
  };
};

const UnleashProvider = ({
  bootstrap,
  session,
  children,
}: {
  bootstrap?: IToggle[];
  children: ReactNode;
  session?: {
    userCompanyEmail: string | null;
    userCompanyEmailDomain: string | null;
  };
}) => {
  const userCompanyEmail = session?.userCompanyEmail || null;
  const userCompanyEmailDomain = session?.userCompanyEmailDomain || null;
  const unleashCtx = session
    ? generateUnleashContext(userCompanyEmail, userCompanyEmailDomain)
    : ({} as ReturnType<typeof generateUnleashContext>);
  return (
    <FlagProvider
      config={{
        ...UNLEASH_CONFIG,
        bootstrap,
        context: {
          ...unleashCtx,
          properties: {
            ...unleashCtx.properties,
            ed_appEnv: process.env.NEXT_PUBLIC_APP_ENV!,
          },
        },
      }}
    >
      <CustomContextProvider session={session}>
        {children}
      </CustomContextProvider>
    </FlagProvider>
  );
};

const CustomContextProvider = ({
  session,
  children,
}: {
  children: ReactNode;
  session?: {
    userCompanyEmail: string | null;
    userCompanyEmailDomain: string | null;
  };
}) => {
  const router = useRouter();
  const pathIsPublic = PUBLIC_PATHS.includes(router.pathname);
  const onboardingPath = ONBOARDING_PATHS.includes(router.pathname);
  const subscriptionPath = SUBSCRIPTION_PATHS.includes(router.pathname);
  const updateContext = useUnleashContext();

  const { data: userCompanyData } = useMyUserCompanyQuery({
    options: {
      enabled:
        !pathIsPublic && !onboardingPath && !subscriptionPath && !session,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  });

  const userCompanyEmail = userCompanyData?.data?.result?.email || null;
  const userCompanyEmailDomain =
    userCompanyData?.data?.result?.company?.emailDomain || null;

  useEffect(() => {
    const updateUnleashContext = async () => {
      await updateContext(
        generateUnleashContext(userCompanyEmail, userCompanyEmailDomain),
      );
    };

    if (userCompanyEmail && !session) updateUnleashContext();
  }, [userCompanyEmail, userCompanyEmailDomain]);

  return <MaintenanceChecker>{children}</MaintenanceChecker>;
};

const MaintenanceChecker = ({ children }) => {
  const { pathname } = useRouter();
  const maintenanceVariant = useVariant(UNLEASH_TOGGLES.ed_maintenance);
  const parsedPayload = JSON.parse(maintenanceVariant.payload?.value ?? '{}');

  const maintenanceEnabled = maintenanceVariant.enabled;
  const maintenanceTarget = parsedPayload?.target as 'all' | 'specific';
  const maintenanceRoutes = parsedPayload?.routes as string[];

  if (
    maintenanceEnabled &&
    (maintenanceTarget === 'all' ||
      (maintenanceTarget === 'specific' &&
        maintenanceRoutes.includes(pathname)))
  ) {
    return <Maintenance />;
  }

  return children;
};

export default UnleashProvider;
